import React, { useMemo } from 'react';
import Button from '@/components/UI/Button';
import DeleteButton from '@/components/UI/DeleteButton';
import EditButton from '@/components/UI/EditButton';
import EmptyState from '@/components/UI/EmptyState';
import type { GovernanceStrategyLimitDraft } from './governanceHelpers';

export interface GovernanceStrategyOption {
    strategyId: number;
    name: string;
}

interface GovernanceStrategyLimitsTableProps {
    strategyLimits: GovernanceStrategyLimitDraft[];
    strategies: GovernanceStrategyOption[];
    onAdd: () => void;
    onEdit: (strategyId: number) => void;
    onDelete: (strategyId: number) => void;
}

const formatCap = (value: number | null): string => (value === null ? 'No cap' : value.toString());

/** Editable aggregate position caps for individual subscribed strategies. */
const GovernanceStrategyLimitsTable: React.FC<GovernanceStrategyLimitsTableProps> = ({
    strategyLimits,
    strategies,
    onAdd,
    onEdit,
    onDelete
}) => {
    const strategyNameById = useMemo(() => new Map(strategies.map((strategy) => [strategy.strategyId, strategy.name])), [strategies]);
    const canAdd = strategies.length > 0 && strategyLimits.length < strategies.length;

    return (
        <div className="border-t border-zinc-800 pt-4">
            <div className="mb-2 flex items-center justify-between">
                <div>
                    <h6 className="text-base font-semibold">Per-strategy limits</h6>
                    <p className="text-sm text-zinc-500">Cap how many concurrent positions one subscribed strategy may occupy.</p>
                </div>
                <Button disabled={!canAdd} variant="link" onClick={onAdd}>
                    + Add
                </Button>
            </div>

            {strategyLimits.length === 0 ? (
                <EmptyState size="sm" title="No per-strategy limits configured." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse bg-zinc-800 text-white">
                        <thead>
                            <tr className="bg-zinc-700 text-left">
                                <th className="px-3 py-2">Strategy</th>
                                <th className="px-3 py-2">Max positions</th>
                                <th className="px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {strategyLimits.map((limit, index) => (
                                <tr className={index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'} key={limit.strategyId}>
                                    <td className="border-t border-zinc-700 px-3 py-2">
                                        {strategyNameById.get(limit.strategyId) ?? `Strategy #${limit.strategyId}`}
                                    </td>
                                    <td className="border-t border-zinc-700 px-3 py-2">{formatCap(limit.maxOpenPositionsPerStrategy)}</td>
                                    <td className="border-t border-zinc-700 px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <EditButton size="sm" onClick={() => onEdit(limit.strategyId)} />
                                            <DeleteButton size="sm" onClick={() => onDelete(limit.strategyId)} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default GovernanceStrategyLimitsTable;
